import {shallowEqual, useSelector} from 'react-redux';

import {VersionVariants} from '@jobseeker/lotus/enums';
import CTA from '@jobseeker/lotus/components/CTA';
import FeaturedInstitute from '@jobseeker/feature-ads/components/AdsBanner/FeaturedInstitutes/V2';
import LearnGrow from '@jobseeker/feature-ads/components/AdsBanner/LearnGrow/V2';
import VerticalFrame from '@jobseeker/feature-ads/components/AdsBanner/VerticalFrame/V2';
import {GoldPartners} from '@jobseeker/feature-ads/components/AdsBanner';
import {PlatinumPartners} from '@jobseeker/feature-ads/components/AdsBanner';
import {getRefEmbedded} from '@jobseeker/common-core/utils/refTrack';

import {AdsPanelType} from '../adCommonPanel.types';
import type {AppState, ProfileTags} from '@jobseeker/common-core/types';

import {AdBannerComponent} from '@jobseeker/feature-ads/components/AdsBanner/Banner';
import {Box} from '@mui/material';
import {TrackEventUBA, getUbaPageName} from '@jobseeker/common-core/utils';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {
  CATEGORY,
  ENGINEERS_WEEK,
  JOBFEED,
  TAG,
  WOMEN_AT_WORK,
} from '@jobseeker/common-core/constants';

const AdsCommonPanel = ({
  showLearnGrow,
  showLearningCenterCTA,
  showFeatured,
  showLearning,
  showEventOnly,
  showFeaturedInstitute,
  showRolesAtLeadingBrands,
  showPlatinumPartners,
  showWowPlatinumPartners,
  showWowGoldPartners,
  showWowFeaturedEmployer,
  showGoldPartners,
  showSalesEvent,
  page,
}: AdsPanelType) => {
  const interceptor: any = useInterceptor();

  const {
    ads: {
      featuredInstitutes,
      rolesAtLeadingBrands,
      platinumPartners,
      goldPartners,
      proTalentGold,
      proTalentPlatinum,
      featuredEmployer,
      learningPartners,
      diversity,
      events,
      proTalentCategory,
      jobfeedBanner,
      advertisementBanner,
      tempGoldSachBanner,
      learnGrow,
      wowGoldPartners,
      wowPlatinumPartners,
      wowFeaturedEmployer,
    },
    gaEvents: {ads: adsGaEvent},
  } = interceptor;

  const featuredEmployerInfo = useSelector(
    (state: AppState) => state.ads.featureEmployer,
    shallowEqual
  );

  const diversityInfo = useSelector(
    (state: AppState) => state.ads.diversity,
    shallowEqual
  );

  const rolesAtLeadingBrandsInfo = useSelector(
    (state: AppState) => state.ads.rolesAtLeadingBrands,
    shallowEqual
  );
  const platinumPartnersInfo = useSelector(
    (state: AppState) => state.ads.platinumPartners,
    shallowEqual
  );
  const wowPlatinumPartnersInfo = useSelector(
    (state: AppState) => state.ads.wowPlatinumPartners,
    shallowEqual
  );
  const wowFeaturedEmployerInfo = useSelector(
    (state: AppState) => state.ads.wowFeaturedEmployer,
    shallowEqual
  );

  const learningPartnersInfo = useSelector(
    (state: AppState) => state.ads.learningPartners,
    shallowEqual
  );

  const pageName = useSelector(
    (state: AppState) => state.app.page,
    shallowEqual
  );

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory,
    shallowEqual
  )?.toString();

  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );
  const {tags} = useSelector(
    (state: AppState) => state.user.profile,
    shallowEqual
  );
  const tagIds =
    tags && tags?.length
      ? tags?.map((tag: ProfileTags) => tag?.tagId).join(',')
      : '';
  const trackEvent = () => {
    TrackEventUBA('learningCentreClick', 'click', {
      categoryID: pageName == 'category' ? Number(categoryId) : undefined,
      tagID: pageName == 'tag' ? Number(tagId) : undefined,
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  };

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  return (
    <>
      {!showFeatured && advertisementBanner?.enabled && (
        <Box mb={2}>
          <AdBannerComponent
            data-testid="jobdetail_advertisement_banner"
            adParams={advertisementBanner}
            adsEventInfo={adsGaEvent['advertisementBanner']}
          />
        </Box>
      )}
      {showFeatured && (
        <>
          {featuredEmployer?.enabled && (
            <VerticalFrame
              data-testid="jobfeed_course_featured_employer"
              adParams={featuredEmployer}
              adsInfo={featuredEmployerInfo}
              direction="column"
              source="featuredEmployer"
              options={{
                categoryIds:
                  page == 'course' || page == 'company' ? '' : categoryId,
              }}
              adsEventInfo={adsGaEvent['featuredEmployer']}
              titleOut={isAppIimjobs ? false : true}
            />
          )}

          {diversity?.enabled && (
            <VerticalFrame
              data-testid="jobfeed_course_diversity"
              adParams={diversity}
              adsInfo={diversityInfo}
              source="diversity"
              direction="column"
              adsEventInfo={adsGaEvent['diversityEmployer']}
              titleOut={isAppIimjobs ? false : true}
            />
          )}

          {advertisementBanner?.enabled && (
            <AdBannerComponent
              data-testid="jobfeed_advertisement_banner"
              adParams={advertisementBanner}
              adsEventInfo={adsGaEvent['advertisementBanner']}
            />
          )}

          {tempGoldSachBanner?.enabled && (
            <AdBannerComponent
              data-testid="joblist_tempGoldSach_banner"
              adParams={tempGoldSachBanner}
              adType="tempGoldSachBanner"
              adsEventInfo={adsGaEvent['tempGoldSachBanner']}
            />
          )}

          {isAppIimjobs && events?.enabled && (
            <FeaturedInstitute
              data-testid="event_advertise_banner"
              slot={events?.slot ?? ''}
              titleOut={isAppIimjobs ? false : true}
              label=""
              type="event"
            />
          )}
        </>
      )}

      {!showFeatured && tempGoldSachBanner?.enabled && (
        <AdBannerComponent
          data-testid="joblist_tempGoldSach_banner"
          adParams={tempGoldSachBanner}
          adType="tempGoldSachBanner"
          adsEventInfo={adsGaEvent['tempGoldSachBanner']}
        />
      )}

      {showEventOnly && events?.enabled && (
        <FeaturedInstitute
          data-testid="event_advertise_banner"
          slot={events?.slot ?? ''}
          titleOut={isAppIimjobs ? false : true}
          label=""
          type="event"
          adsEventInfo={adsGaEvent['eventBanner']}
        />
      )}

      {isAppIimjobs &&
        (pageName === TAG || pageName === CATEGORY) &&
        proTalentCategory?.enabled && (
          <FeaturedInstitute
            data-testid="pro_talent_category"
            slot={proTalentCategory?.slot ?? ''}
            titleOut={isAppIimjobs ? false : true}
            label=""
            fixHeight={false}
            maxAds={500}
            type="event"
            companyBannerEvent={true}
          />
        )}

      {isAppIimjobs && pageName === JOBFEED && jobfeedBanner?.enabled && (
        <FeaturedInstitute
          data-testid="jobfeed_banner"
          slot={jobfeedBanner?.slot ?? ''}
          titleOut={isAppIimjobs ? false : true}
          label={jobfeedBanner?.label ?? ''}
          maxAds={500}
          fixHeight={false}
          options={{tags: tagIds}}
          type="event"
          companyBannerEvent={true}
        />
      )}

      {isAppIimjobs &&
        rolesAtLeadingBrands?.enabled &&
        pageName === JOBFEED &&
        showRolesAtLeadingBrands && (
          <VerticalFrame
            data-testid="jobfeed_roles_at_leading_brands"
            adParams={rolesAtLeadingBrands}
            adsInfo={rolesAtLeadingBrandsInfo}
            source="rolesAtLeadingBrands"
            direction="column"
            adsEventInfo={adsGaEvent['rolesAtLeadingBrands']}
            titleOut={isAppIimjobs ? false : true}
          />
        )}
      {!isAppIimjobs &&
        platinumPartners?.enabled &&
        pageName === ENGINEERS_WEEK &&
        showPlatinumPartners && (
          <Box
            data-testid="engineer_work_platinum_partners"
            sx={{marginLeft: {xs: 1.5, md: 0}}}
          >
            <PlatinumPartners
              slot={platinumPartners?.slot ?? ''}
              label={platinumPartners?.label || 'Hiring Partners'}
              adsEventInfo={adsGaEvent['platinum_partners']}
            />
          </Box>
        )}
      {showWowPlatinumPartners && (
        <Box
          data-testid="wow_platinum_partners"
          sx={{
            marginLeft: {xs: 1.5, md: 0},
            marginRight: isAppIimjobs
              ? {xxs: '-2px', sm: 0}
              : page === WOMEN_AT_WORK
              ? isAppIimjobs
                ? '16px'
                : {xs: '0px', sm: '16px'}
              : '-18px',
          }}
        >
          <PlatinumPartners
            slot={wowPlatinumPartners?.slot ?? ''}
            label={wowPlatinumPartners?.label || 'Hiring Partners'}
            adsEventInfo={adsGaEvent['wowPlatinumPartners']}
          />
        </Box>
      )}
      {!isAppIimjobs &&
        goldPartners?.enabled &&
        pageName === ENGINEERS_WEEK &&
        showGoldPartners && (
          <Box
            data-testid="engineer_work_gold_partners"
            sx={{
              marginLeft: {xs: 1.5, md: 0},
              marginRight: {xs: 1.5, md: 0},
              minHeight: '33px',
            }}
          >
            <GoldPartners
              label={goldPartners?.label}
              titleOut={true}
              adsEventInfo={adsGaEvent['gold_partners']}
              slot={goldPartners?.slot ?? ''}
            />
          </Box>
        )}
      {page === WOMEN_AT_WORK &&
        showWowGoldPartners &&
        wowGoldPartners?.enabled && (
          <Box
            data-testid="wow_gold_partners"
            sx={{
              marginLeft: {xs: 1.5, md: 0},
              marginRight: {xs: 1.5, md: 0},
              minHeight: '33px',
            }}
          >
            <GoldPartners
              label={wowGoldPartners?.label}
              titleOut={isAppIimjobs ? false : true}
              adsEventInfo={adsGaEvent['gold_partners']}
              slot={wowGoldPartners?.slot ?? ''}
            />
          </Box>
        )}
      {page === WOMEN_AT_WORK &&
        showWowFeaturedEmployer &&
        wowFeaturedEmployer?.enabled && (
          <VerticalFrame
            data-testid="wow_featured_employer"
            adParams={wowFeaturedEmployer}
            adsInfo={wowFeaturedEmployer}
            direction="column"
            source="wowFeaturedEmployer"
            options={{}}
            adsEventInfo={adsGaEvent['wowFeaturedEmployer']}
            titleOut={isAppIimjobs ? false : true}
          />
        )}

      {showLearningCenterCTA && (
        <Box>
          <CTA
            onClick={trackEvent}
            actionUrl={getRefEmbedded(
              process.env.NEXT_PUBLIC_LEARNING_CENTER || '',
              page ? 'LEARNING_CENTER_JD' : 'LEARNING_CENTER_CAT',
              'normal'
            )}
            subTitle="UPSKILL YOURSELF"
            actionLabel="Explore Courses"
            title="My Learning Centre"
            cardSxProps={{
              my: showFeatured ? 2 : 0,
              mb: isAppIimjobs ? 2 : 3.75,
              borderRadius: isAppIimjobs ? 4 : 2,
            }}
            data-testid="jobfeed_course_learning_center_cta"
            variant={VersionVariants.VERSION_TWO}
          />
        </Box>
      )}

      {learningPartners?.enabled && showLearning && (
        <VerticalFrame
          adParams={{...learningPartners, ...{showTitle: true}}}
          adsInfo={learningPartnersInfo}
          direction="row"
          adsEventInfo={adsGaEvent['learningPartners']}
          data-testid="jobfeed_course_learning_partners"
        />
      )}

      {featuredInstitutes?.enabled && showFeaturedInstitute && (
        <FeaturedInstitute
          {...featuredInstitutes}
          titleOut={isAppIimjobs ? false : true}
          data-testid="jobfeed_course_featured_institute"
          adsEventInfo={adsGaEvent['featuredInstitutes']}
        />
      )}

      {isAppIimjobs && proTalentPlatinum?.enabled && showSalesEvent && (
        <Box sx={{mt: -2}}>
          <FeaturedInstitute
            {...proTalentPlatinum}
            data-testid="pro_talent_platinum_advertise_banner"
            titleOut={isAppIimjobs ? false : true}
            type="event"
            fixHeight={false}
            maxAds={500}
            adsEventInfo={adsGaEvent['proTalentPlatinum']}
            companyBannerEvent={true}
          />
        </Box>
      )}

      {isAppIimjobs && proTalentGold?.enabled && showSalesEvent && (
        <FeaturedInstitute
          {...proTalentGold}
          data-testid="pro_talent_gold_advertise_banner"
          titleOut={isAppIimjobs ? false : true}
          type="event"
          maxAds={500}
          fixHeight={false}
          adsEventInfo={adsGaEvent['proTalentGold']}
          companyBannerEvent={true}
        />
      )}

      {learnGrow?.enabled && showLearnGrow && (
        <LearnGrow
          data-testid="jobfeed_learn_and_grow"
          titleOut={isAppIimjobs ? false : true}
          adsEventInfo={adsGaEvent['learningCourses']}
          {...learnGrow}
        />
      )}
    </>
  );
};

export default AdsCommonPanel;
